import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Sparkle, CheckCircle, Check, MapPin, Storefront, Robot, Lightning,
  ChartLineUp, Trophy, Warning, CalendarBlank, ArrowsClockwise, Crosshair,
  Rocket, PaintBrush, CurrencyDollar, FileText, PhoneCall, X, Brain,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import SEOHead from "@/lib/seo/SEOHead";

// SWAP THIS to your real Calendly URL when ready (used when the demo isn't agency-branded)
const CALENDLY_URL = "https://calendly.com/ranktrust/strategy-session";

const PRIMARY_DEFAULT = "#2D5BFF";
const ACCENT  = "#65CE95";
const DARK    = "#0F1311";
const GOLD    = "#F59E0B";
const RED     = "#EF4444";

// Default RankTrust logo (when no agency override is provided)
const RANKTRUST_LOGO = "https://customer-assets.emergentagent.com/job_gbp-top-3/artifacts/mlg09j9p_Ranktrust%20new%20logo.png";

// Loading narrative — appears one line at a time while the real visibility-check runs
const LOADING_STEPS = [
  { icon: "🔍", text: "Connecting to Google Business Profile…" },
  { icon: "📊", text: "Analyzing trust signals…" },
  { icon: "🏆", text: "Comparing top-3 competitors in your market…" },
  { icon: "🤖", text: "Scanning ChatGPT, Claude & Perplexity for mentions…" },
  { icon: "📍", text: "Mapping local visibility gaps…" },
  { icon: "💰", text: "Estimating missed revenue opportunity…" },
];

// Deterministic pseudo-random from a string seed (keeps the audit stable for one (biz, city) combo)
function seededHash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; }
  return h;
}
function rng(seed) { let x = seed; return () => { x = (x * 1664525 + 1013904223) >>> 0; return x / 4294967295; }; }

function fakeAuditFromInputs({ business_name, city, service }) {
  const r = rng(seededHash(`${business_name}|${city}|${service}`.toLowerCase()));
  const trust = 58 + Math.floor(r() * 22);            // 58-79
  const relevance = 55 + Math.floor(r() * 25);        // 55-79
  const comp1 = 84 + Math.floor(r() * 12);
  const comp2 = 80 + Math.floor(r() * 10);
  const comp3 = 78 + Math.floor(r() * 8);
  const missedSearches = 800 + Math.floor(r() * 1400);
  const missedLeads = 18 + Math.floor(r() * 38);
  return { trust, relevance, comp1, comp2, comp3, missedSearches, missedLeads };
}

/* ==================== AI Growth Blueprint — Intelligence Layer ====================
   Pool of 12 possible ranking constraints. For each business, we deterministically
   pick 3 based on a hash of `{biz}|{city}|{service}`. Every business gets a genuinely
   different, priority-ordered blueprint. Reviews are ONE item in the pool with a lower
   pickWeight — so they only appear when their combined score outranks others.
   Later this same interface can be swapped for a live LLM call (Claude/GPT) without
   any UI changes. */
const BLUEPRINT_POOL = [
  {
    key: "service-relevance",
    title: "Weak Service Relevance",
    baseImpact: "HIGH",
    confRange: [88, 96],
    pickWeight: 0.95,
    why: (b, s, c) => `${b}'s primary categories and website content are sending weaker relevance signals than the businesses currently occupying the Top 3 for ${s}${c}. Google is rewarding tighter category alignment and stronger supporting content — this is the semantic tie-break.`,
    how: (b, s, c) => [
      `Update your primary GBP category to match "${s}" precisely`,
      "Add every applicable secondary service category your Top-3 competitors are using",
      `Rewrite your primary service page around "${s}${c}" with local proof points`,
      "Add LocalBusiness + Service schema markup to the page",
      "Strengthen internal links from your home page and blog into that service page",
    ],
    outcome: "Improved local relevance, stronger map-pack competitiveness, and higher qualified click-through for the searches that drive real revenue.",
  },
  {
    key: "topical-authority",
    title: "Poor Topical Authority",
    baseImpact: "HIGH",
    confRange: [82, 93],
    pickWeight: 0.9,
    why: (b, s) => `Top-3 competitors have significantly stronger service-specific content and internal linking supporting their Google Business Profile. Without matching topical depth, ${b} loses the semantic tie-break Google uses to decide who ranks for ${s}.`,
    how: (b, s) => [
      `Publish 3–5 pillar articles on ${s}-specific subtopics (cost, process, options, materials)`,
      `Build a dedicated FAQ hub answering the top 20 questions people search around ${s}`,
      "Interlink all new content back to your primary service page and GBP-linked URL",
      "Add author bios with credentials on service pages to signal E-E-A-T",
      "Earn 2–3 local mentions (chamber, industry association, local press) linking to your topic hub",
    ],
    outcome: "Greater authority and eligibility for the high-value search terms that drive qualified inquiries.",
  },
  {
    key: "gbp-optimization",
    title: "Missing GBP Optimization",
    baseImpact: "HIGH",
    confRange: [80, 92],
    pickWeight: 0.88,
    why: (b) => `${b}'s Google Business Profile is under-utilizing native ranking surfaces: services, attributes, Q&A, posts, and product tiles. Top-3 competitors are populating every available field, sending Google more signals about relevance and activity.`,
    how: (b, s) => [
      `Populate every "Services" entry with a full description tied to ${s}`,
      "Answer the top 10 Q&A prompts on your GBP proactively (before customers do)",
      "Publish weekly GBP Posts with photos and offer callouts to signal activity",
      "Complete every attribute (accessibility, payment, amenities) — Google rewards profile depth",
      "Upload fresh geo-tagged photos monthly (interior, exterior, team, work-in-progress)",
    ],
    outcome: "Stronger native GBP signals, better CTR from map pack, and more inbound calls directly from search.",
  },
  {
    key: "entity-consistency",
    title: "Entity Inconsistencies (NAP)",
    baseImpact: "HIGH",
    confRange: [78, 90],
    pickWeight: 0.7,
    why: (b, s, c) => `${b}'s name, address, and phone number appear inconsistently across major citation sources. Google interprets each mismatch as a signal-quality problem${c ? " for" + c + " searches" : ""}. This is invisible to owners but heavily weighted by the local algorithm.`,
    how: (b) => [
      "Audit top-25 citations (Yelp, YellowPages, Apple Maps, BBB, industry directories)",
      `Standardize the exact spelling, punctuation, and formatting of "${b}" across all listings`,
      "Fix phone number formatting inconsistencies (parentheses, dashes, country codes)",
      "Claim and verify unclaimed listings — abandoned profiles hurt entity trust",
      "Remove duplicate listings, especially the ones with outdated addresses",
    ],
    outcome: "Restored entity trust with Google, cleaner ranking signals, and elimination of a common Top-3 blocker.",
  },
  {
    key: "citation-signals",
    title: "Citation Signal Gaps",
    baseImpact: "MEDIUM",
    confRange: [68, 82],
    pickWeight: 0.6,
    why: (b) => `${b} is missing citations on 30–40% of the directories Top-3 competitors are listed on. Volume + relevance of citations still matter — they're a foundational trust signal Google references when evaluating an unfamiliar local business.`,
    how: (b, s) => [
      `Build citations on the 15 industry-specific directories for ${s} providers`,
      "Add listings on the 10 highest-authority general local directories (Yelp, BBB, Foursquare, Bing Places)",
      `Get listed on 3–5 local chamber-of-commerce and community directories`,
      "Ensure every citation includes the same website URL that Google Business Profile links to",
      "Include categories and services in every citation description — not just NAP",
    ],
    outcome: "Broader citation footprint, stronger foundational trust, and better co-occurrence signals across the web.",
  },
  {
    key: "internal-linking",
    title: "Weak Internal Linking Structure",
    baseImpact: "MEDIUM",
    confRange: [65, 80],
    pickWeight: 0.55,
    why: (b, s) => `${b}'s website flattens topical authority by linking every page equally instead of concentrating link equity on the ${s} service page that maps to your primary GBP category. Google reads this as ambiguity about what you're best at.`,
    how: (b, s) => [
      `Build a clear hub-and-spoke structure with the ${s} page at the center`,
      "Link every FAQ, blog post, and case study back to the primary service page with topical anchor text",
      "Reduce navigation clutter — every extra site-wide link dilutes link equity",
      "Add contextual in-body links (not just menus) from high-authority pages to the service page",
      "Break up long service pages into sub-services with clear parent-child linking",
    ],
    outcome: "Concentrated page authority on your money-maker, clearer topical signals, and improved rankings for high-intent queries.",
  },
  {
    key: "local-content",
    title: "Local Content Gap",
    baseImpact: "MEDIUM",
    confRange: [72, 84],
    pickWeight: 0.7,
    why: (b, s, c) => `${b}'s website lacks the geo-specific content Google uses to confirm local relevance${c ? " for" + c : ""}. Top-3 competitors have neighborhood-level content, local landmarks, and community involvement content that reinforces the "we're actually here" signal.`,
    how: (b, s, c) => [
      `Build a "${s}${c || ""}" landing page with hyperlocal proof (neighborhoods, landmarks, driving directions)`,
      `Publish 2–3 case studies featuring local clients (with permission)`,
      "Add a photo gallery tagged with EXIF geo-coordinates from real work sites",
      "Sponsor or attend one local event per quarter and publish a recap page",
      "Add embed of your GBP map and driving directions on the primary service page",
    ],
    outcome: "Reinforced local relevance signals, higher click-through from map pack, and stronger conversion for near-me searches.",
  },
  {
    key: "trust-signals",
    title: "Weak Customer Trust Signals",
    baseImpact: "MEDIUM",
    confRange: [70, 85],
    pickWeight: 0.75,
    why: (b) => `${b}'s trust signals (reviews, testimonials, certifications, guarantees, response velocity) are weaker than Top-3 competitors when Google weighs them holistically. Trust is a broader signal than just review count — reviews are one component of a larger authority stack.`,
    how: (b) => [
      "Publish 8–12 written testimonials with client names, photos, and specific results",
      "Add trust bars: certifications, awards, years in business, licensed/insured badges",
      "Feature a satisfaction guarantee prominently on service pages",
      "Respond to every review (positive and negative) within 24 hours",
      "Add a live phone-answering commitment or SLA on your homepage",
    ],
    outcome: "Elevated customer authority, higher on-page conversion, and stronger holistic trust weighting from Google's algorithm.",
  },
  {
    key: "behavioral-signals",
    title: "Behavioral Signal Deficit",
    baseImpact: "MEDIUM",
    confRange: [60, 75],
    pickWeight: 0.55,
    why: (b) => `${b}'s user-engagement signals (CTR from search, dwell time, direct-navigation intent) trail Top-3 competitors. Google's local algorithm now factors behavioral outcomes — when searchers click competitors more, or bounce back to results after visiting your site, rankings suffer.`,
    how: () => [
      "Rewrite your GBP business description with a benefit-driven hook in the first 12 words",
      "Add compelling GBP photos as the first 3 gallery images (they drive CTR from map pack)",
      "Add a bold, above-fold hero CTA on your service page (phone + book online)",
      "Reduce page load time to under 2s on mobile (behavioral signal)",
      "Remove or fix any low-engagement pages — thin content drags site-wide signals",
    ],
    outcome: "Better CTR from map pack, longer dwell time, and improved behavioral inputs to Google's local ranking model.",
  },
  {
    key: "service-area",
    title: "Service Area Optimization",
    baseImpact: "MEDIUM",
    confRange: [65, 80],
    pickWeight: 0.6,
    why: (b, s, c) => `${b}'s service area definition on GBP and website is too broad or too narrow relative to actual ranking-viable distance from${c || " the primary market"}. Overly broad service areas dilute proximity signals; too narrow limits eligible searches.`,
    how: (b, s, c) => [
      "Set GBP service area to a realistic 10–15 mile radius (or top 3–5 zip codes)",
      `Create dedicated landing pages for the 3 highest-value neighborhoods${c ? " in" + c : ""}`,
      "Remove service areas where you're unlikely to rank in the near term",
      "Add zip-code and neighborhood mentions naturally throughout the primary service page",
      "Ensure the service area on GBP matches the coverage claimed on your website",
    ],
    outcome: "Sharper proximity signals, better eligibility for near-me searches, and improved map-pack visibility in target markets.",
  },
  {
    key: "conversion-signals",
    title: "Website Conversion Signals",
    baseImpact: "LOW",
    confRange: [55, 72],
    pickWeight: 0.5,
    why: (b) => `${b}'s website is losing qualified traffic due to weak conversion signals: unclear CTAs, buried phone numbers, slow load, or intrusive forms. Google increasingly infers business quality from post-click behavior, and low conversion depresses long-term ranking momentum.`,
    how: () => [
      "Add a sticky click-to-call bar on mobile with the business phone number",
      "Move primary CTA above the fold on every service page",
      "Add a 3-field lead form (name, phone, service) — remove all optional fields",
      "Compress hero images and enable next-gen formats (WebP/AVIF)",
      "Add trust badges and response-time promise directly next to the form",
    ],
    outcome: "Higher lead-conversion rate, better post-click signals, and stronger long-term ranking momentum.",
  },
  {
    key: "review-velocity",
    title: "Low Review Velocity",
    baseImpact: "MEDIUM",
    confRange: [50, 68],
    pickWeight: 0.38,  // deliberately lower — reviews are ONE signal, not the strategy
    why: (b) => `${b}'s review acquisition rate is lower than competing businesses. Reviews are one ranking signal — not the ranking strategy — but consistent velocity strengthens trust and engagement signals over time. This appears in your blueprint only when the AI ranks it above other constraints for this specific business.`,
    how: () => [
      "Set up an automated post-appointment SMS asking for a Google review within 24 hours",
      "Add a review-request QR code to invoices, business cards, and the reception counter",
      "Train front-desk staff with a one-line ask for satisfied clients (highest close rate)",
      "Respond to every review (positive and negative) within 24 hours to signal engagement",
      "Target 5–8 new reviews per month over the next 90 days",
    ],
    outcome: "Improved trust component of the ranking model and compounding engagement momentum month-over-month.",
  },
];

function buildBlueprint({ business_name, city, service }) {
  const biz = business_name || "This business";
  const svc = service || "your primary service";
  const cityFragment = city ? ` in ${city}` : "";
  const seed = seededHash(`${biz}|${city || ""}|${svc}`.toLowerCase());
  const r = rng(seed);

  // Score each pool item; deterministic per business.
  const scored = BLUEPRINT_POOL.map((item, i) => {
    const jitter = 0.55 + r() * 0.9;  // 0.55–1.45 multiplier
    const conf   = Math.round(item.confRange[0] + r() * (item.confRange[1] - item.confRange[0]));
    return { ...item, _score: item.pickWeight * jitter, _conf: conf };
  });

  // Pick top 3 by score, then re-sort by confidence descending for display.
  scored.sort((a, b) => b._score - a._score);
  const picked = scored.slice(0, 3).sort((a, b) => b._conf - a._conf);

  return picked.map((item) => ({
    key: item.key,
    title: item.title,
    impact: item.baseImpact,
    confidence: item._conf,
    why: item.why(biz, svc, cityFragment),
    how: item.how(biz, svc, cityFragment),
    outcome: item.outcome,
  }));
}


function gradeFromRate(rate) {
  if (rate >= 75) return "A";
  if (rate >= 60) return "B+";
  if (rate >= 45) return "B-";
  if (rate >= 30) return "C+";
  return "C";
}

function colorForScore(n) {
  if (n >= 80) return ACCENT;
  if (n >= 65) return GOLD;
  return RED;
}

const Logo = ({ brand }) => {
  const logoSrc = brand?.logo_url || RANKTRUST_LOGO;
  const tagline = brand?.tagline || "The Business Growth Intelligence Platform";
  return (
    <div style={{ display: "inline-block" }}>
      <img
        src={logoSrc}
        alt={brand?.brand_name || "RankTrust"}
        style={{ height: 32, width: "auto", display: "block", maxWidth: 180 }}
      />
      <div style={{ fontSize: 9, letterSpacing: "1.5px", color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", marginTop: 3, whiteSpace: "nowrap" }}>
        {tagline}
      </div>
    </div>
  );
};

/** Animated number counter that ticks up to `target` over `duration` ms */
function Counter({ target, duration = 1400, prefix = "", suffix = "", className = "" }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return <span className={className} style={{ fontVariantNumeric: "tabular-nums" }}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

/** Vertical reveal — children animate in one-by-one as the parent mounts each */
function RevealCard({ children, delay = 0, testId }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div
      data-testid={testId}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(16px)",
        transition: "opacity .6s ease, transform .6s ease",
      }}
    >
      {children}
    </div>
  );
}

export default function Demo() {
  const [params] = useSearchParams();
  const { slug } = useParams(); // present when route is /a/:slug/demo
  const [brand, setBrand] = useState(null); // null = default RankTrust branding
  const [form, setForm] = useState({
    business_name: params.get("biz") || "",
    city: params.get("city") || "",
    state: params.get("state") || "",
    service: params.get("service") || "",
  });
  const [phase, setPhase] = useState("intake"); // intake → analyzing → results
  const [loadStep, setLoadStep] = useState(0);
  const [audit, setAudit] = useState(null);
  const [realCheck, setRealCheck] = useState(null); // real /api/public/visibility-check result
  const cancelRef = useRef(false);

  // Resolve white-label agency brand when accessed via /a/:slug/demo
  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/public/agency-brand/${slug}`).then(({ data }) => {
      setBrand(data);
      document.title = `${data.brand_name} | AI Visibility Audit`;
    }).catch(() => { /* invalid slug → fall back to default RankTrust */ });
  }, [slug]);

  const PRIMARY = brand?.primary_color || PRIMARY_DEFAULT;
  const calendlyUrl = brand?.calendly_url || CALENDLY_URL;

  // Auto-start if all 3 params present (so IntentBrain SMS link can prefill+autostart)
  useEffect(() => {
    if (params.get("auto") === "1" && form.business_name && form.city && form.service) {
      // small delay so users see the prefilled form for ~1s before it auto-runs
      const t = setTimeout(() => runAudit(), 900);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAudit = async () => {
    if (!form.business_name.trim() || !form.city.trim() || !form.service.trim()) {
      toast.error("Business name, city, and main service are required");
      return;
    }
    cancelRef.current = false;
    setPhase("analyzing");
    setLoadStep(0);
    const fake = fakeAuditFromInputs(form);
    setAudit(fake);

    // Kick off the REAL visibility check in parallel (hybrid mode).
    // The narrative steps below take ~9s, which usually covers the ChatGPT call.
    const realPromise = axios
      .post(`${API}/public/visibility-check`, {
        business_name: form.business_name,
        keyword: form.service,
        city: form.city,
        state: form.state || "",
      })
      .then((r) => r.data)
      .catch(() => null);

    // Walk through the loading steps
    for (let i = 0; i < LOADING_STEPS.length; i++) {
      if (cancelRef.current) return;
      setLoadStep(i);
      await new Promise((r) => setTimeout(r, 1500));
    }

    // Wait up to ~4 more seconds for the real check to finish; fall through with null otherwise
    const realResult = await Promise.race([
      realPromise,
      new Promise((r) => setTimeout(() => r(null), 4000)),
    ]);
    if (cancelRef.current) return;
    setRealCheck(realResult);
    setPhase("results");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetDemo = () => {
    cancelRef.current = true;
    setPhase("intake");
    setLoadStep(0);
    setAudit(null);
    setRealCheck(null);
  };

  return (
    <div className="min-h-screen" style={{ background: "#05060A", color: "#fff", fontFamily: "Manrope, system-ui, sans-serif" }} data-testid="demo-page">
      {slug ? (
        // Whitelabel duplicate template — noindex,follow. No canonical
        // (the base /demo page owns the canonical for Sample Business
        // Growth Assessment).
        <SEOHead
          title={`${brand?.brand_name || "RankTrust"} | AI Visibility Audit`}
          description="Sample AI Visibility Audit — see how a Business Growth Assessment looks for a local business."
          robots="noindex,follow"
          indexable={false}
        />
      ) : (
        <SEOHead path="/demo" />
      )}
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to={slug ? `/a/${slug}/demo` : "/"}><Logo brand={brand} /></Link>
          {!brand && (
            <Link
              to="/pricing"
              className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition-colors"
              data-testid="demo-pricing"
            >
              See Pricing
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {phase === "intake" && <Intake form={form} setForm={setForm} onRun={runAudit} primary={PRIMARY} />}
        {phase === "analyzing" && <Analyzing step={loadStep} business={form.business_name} city={form.city} primary={PRIMARY} />}
        {phase === "results" && audit && (
          <Results
            form={form}
            audit={audit}
            realCheck={realCheck}
            onReset={resetDemo}
            primary={PRIMARY}
            brand={brand}
            calendlyUrl={calendlyUrl}
          />
        )}
      </main>

      <footer className="py-10 border-t border-white/5 mt-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 space-y-3 text-xs text-zinc-500">
          <div data-testid="footer-legal-entity">
            RankTrust is a division of Card Service Warehouse, Inc., operating under the same legal entity and EIN.
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>© {new Date().getFullYear()} {brand?.brand_name || "RankTrust"}{brand ? " · Powered by RankTrust" : " · Google Business Profile Ranking Intelligence"}</div>
            <div className="flex gap-4">
              <Link to="/terms" className="hover:text-zinc-300">Terms</Link>
              <Link to="/privacy" className="hover:text-zinc-300">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ----------------- Phase 1: Intake ----------------- */
function Intake({ form, setForm, onRun, primary }) {
  const PRIMARY = primary || PRIMARY_DEFAULT;
  return (
    <div className="max-w-2xl mx-auto pt-6 lg:pt-12 text-center">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase mb-6" style={{ background: `${ACCENT}1F`, color: ACCENT }}>
        <Sparkle size={12} weight="fill" /> Live AI Audit · 90 Seconds
      </div>
      <h1 className="font-black tracking-tight leading-[1.02] text-4xl lg:text-6xl" data-testid="demo-h1">
        Find Out Why <span style={{ color: PRIMARY }}>Competitors</span><br /> Outrank You.
      </h1>
      <p className="mt-5 text-zinc-400 text-base lg:text-lg max-w-xl mx-auto leading-relaxed">
        Our AI scans your Google Business Profile, benchmarks you against top-3 competitors,
        and shows where you're losing visibility — across both Google Maps and AI search.
      </p>

      <div className="mt-10 grid gap-3 max-w-md mx-auto text-left">
        <Field
          label="Business name"
          icon={<Storefront size={14} weight="bold" />}
          value={form.business_name}
          onChange={(v) => setForm({ ...form, business_name: v })}
          placeholder="e.g. Atlanta Roofing Experts"
          testId="demo-input-biz"
        />
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Field
              label="City"
              icon={<MapPin size={14} weight="bold" />}
              value={form.city}
              onChange={(v) => setForm({ ...form, city: v })}
              placeholder="Atlanta"
              testId="demo-input-city"
            />
          </div>
          <Field
            label="State"
            value={form.state}
            onChange={(v) => setForm({ ...form, state: v.toUpperCase().slice(0, 3) })}
            placeholder="GA"
            testId="demo-input-state"
          />
        </div>
        <Field
          label="Main service / keyword"
          icon={<Crosshair size={14} weight="bold" />}
          value={form.service}
          onChange={(v) => setForm({ ...form, service: v })}
          placeholder="e.g. emergency roof repair"
          testId="demo-input-service"
        />
        <button
          onClick={onRun}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 px-7 py-4 font-bold text-base rounded-lg text-white transition-all hover:-translate-y-0.5 hover:shadow-2xl"
          style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` }}
          data-testid="demo-run-btn"
        >
          <Sparkle size={16} weight="fill" /> Run AI Visibility Audit
          <ArrowRight size={14} weight="bold" />
        </button>
        <p className="text-[11px] text-zinc-500 text-center mt-1">Free · No credit card · ~90 seconds</p>
      </div>
    </div>
  );
}

function Field({ label, icon, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-zinc-400 mb-1.5 flex items-center gap-1.5">
        {icon}{label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg px-4 py-3 text-white placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors"
      />
    </div>
  );
}

/* ----------------- Phase 2: Analyzing ----------------- */
function Analyzing({ step, business, city, primary }) {
  const PRIMARY = primary || PRIMARY_DEFAULT;
  const progress = Math.min(100, Math.round(((step + 1) / LOADING_STEPS.length) * 100));
  return (
    <div className="max-w-2xl mx-auto pt-10 lg:pt-20 text-center" data-testid="demo-analyzing">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase mb-6 animate-pulse" style={{ background: `${PRIMARY}22`, color: PRIMARY }}>
        <Robot size={12} weight="fill" /> AI Audit Running
      </div>
      <h2 className="font-black tracking-tight leading-[1.05] text-3xl lg:text-5xl">
        Analyzing <span style={{ color: PRIMARY }}>{business}</span><br />
        in {city}…
      </h2>

      {/* Progress bar */}
      <div className="mt-12 max-w-md mx-auto">
        <div className="flex items-center justify-between text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">
          <span>AI Diagnostic</span>
          <span style={{ color: ACCENT }} className="tabular-nums">{progress}%</span>
        </div>
        <div className="h-2 rounded-full bg-zinc-900 overflow-hidden">
          <div
            className="h-full transition-all duration-700"
            style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})` }}
          ></div>
        </div>
      </div>

      {/* Streaming step list */}
      <div className="mt-10 max-w-md mx-auto text-left space-y-2.5">
        {LOADING_STEPS.map((s, i) => {
          const status = i < step ? "done" : i === step ? "active" : "pending";
          return (
            <div
              key={i}
              className="flex items-center gap-3 text-sm transition-all"
              style={{
                opacity: status === "pending" ? 0.3 : 1,
                color: status === "done" ? ACCENT : status === "active" ? "#fff" : "#71717a",
              }}
              data-testid={`demo-step-${i}`}
            >
              <div className="w-6 text-base">
                {status === "done" ? <CheckCircle size={18} weight="fill" /> : status === "active" ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span> : <span>{s.icon}</span>}
              </div>
              <div className={status === "active" ? "font-semibold" : ""}>{s.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------- Phase 3: Results ----------------- */
function Results({ form, audit, realCheck, onReset, primary, brand, calendlyUrl }) {
  const PRIMARY = primary || PRIMARY_DEFAULT;
  const ctaUrl = calendlyUrl || CALENDLY_URL;
  const { trust, relevance, comp1, comp2, comp3, missedSearches, missedLeads } = audit;
  const overall = Math.round((trust + relevance) / 2);

  // Real AI search visibility — fall back to grades derived from fake scores if API failed
  let chatgptGrade, claudeGrade, perplexityGrade;
  let realRate = null;
  if (realCheck?.engines) {
    chatgptGrade = gradeFromRate(realCheck.engines.chatgpt?.mention_rate ?? 0);
    claudeGrade = gradeFromRate(realCheck.engines.claude?.mention_rate ?? 0);
    perplexityGrade = gradeFromRate(realCheck.engines.perplexity?.mention_rate ?? 0);
    realRate = realCheck.overall_mention_rate;
  } else {
    chatgptGrade = gradeFromRate(trust - 5);
    claudeGrade = gradeFromRate(trust - 12);
    perplexityGrade = gradeFromRate(trust - 8);
  }

  return (
    <div className="space-y-6" data-testid="demo-results">
      <RevealCard delay={0} testId="reveal-header">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-1.5">Audit Complete</div>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight">
              {form.business_name} <span className="text-zinc-500 font-bold">·</span> {form.city}
            </h2>
          </div>
          <button
            onClick={onReset}
            className="text-xs font-bold tracking-wider uppercase text-zinc-500 hover:text-white inline-flex items-center gap-1.5 transition-colors"
            data-testid="demo-reset"
          >
            <ArrowsClockwise size={14} weight="bold" /> Run another audit
          </button>
        </div>
      </RevealCard>

      {/* Trust + Relevance dual ring */}
      <RevealCard delay={250} testId="reveal-scores">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ScoreCard label="Trust Score" value={trust} caption="Reviews, profile completeness, NAP consistency, activity." />
          <ScoreCard label="Relevance Score" value={relevance} caption="Categories, services, content alignment with search intent." />
        </div>
      </RevealCard>

      {/* Competitor comparison */}
      <RevealCard delay={650} testId="reveal-competitors">
        <Section label="Competitor Comparison" icon={<Trophy size={14} weight="fill" />}>
          <div className="space-y-2.5">
            {[
              { name: "Top-3 Competitor A", score: comp1, you: false },
              { name: "Top-3 Competitor B", score: comp2, you: false },
              { name: "Top-3 Competitor C", score: comp3, you: false },
              { name: `${form.business_name} (you)`, score: trust, you: true },
            ].map((c, i) => (
              <CompetitorRow key={i} {...c} max={Math.max(comp1, comp2, comp3, trust)} />
            ))}
          </div>
          <p className="mt-4 text-sm text-zinc-400">
            Competitors appear to have stronger trust signals — that gap is the most likely reason they outrank you.
          </p>
        </Section>
      </RevealCard>

      {/* Lost revenue */}
      <RevealCard delay={1050} testId="reveal-revenue">
        <Section label="Estimated Missed Opportunity" icon={<Warning size={14} weight="fill" />} accentColor={RED}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Metric label="Missed local searches / month" value={missedSearches} color={RED} />
            <Metric label="Missed leads / month" value={missedLeads} color={RED} />
          </div>
          <p className="mt-4 text-sm text-zinc-400">
            Businesses outside the top-3 capture less than 30% of local clicks. This compounds every month.
          </p>
        </Section>
      </RevealCard>

      {/* AI search visibility */}
      <RevealCard delay={1500} testId="reveal-ai-visibility">
        <Section label="AI Search Visibility" icon={<Robot size={14} weight="fill" />} accentColor={PRIMARY}>
          <div className="grid grid-cols-3 gap-3">
            <AIBadge engine="ChatGPT" grade={chatgptGrade} color="#10A37F" />
            <AIBadge engine="Claude" grade={claudeGrade} color="#D97706" />
            <AIBadge engine="Perplexity" grade={perplexityGrade} color="#20808D" />
          </div>
          <p className="mt-4 text-sm text-zinc-400">
            {realCheck
              ? `AI engines mentioned your business ${realRate}% of the time across customer-intent prompts. Many businesses rank on Google but are rarely recommended by AI.`
              : "AI search is becoming a major source of local discovery. Most local businesses are invisible to it."}
          </p>
        </Section>
      </RevealCard>

      {/* Overall + CTA */}
      <RevealCard delay={1900} testId="reveal-overall">
        <div className="rounded-2xl p-8 lg:p-10 text-center" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` }}>
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/80 mb-3">Overall Visibility Score</div>
          <div className="text-7xl lg:text-8xl font-black tabular-nums leading-none">
            <Counter target={overall} />
            <span className="text-3xl lg:text-4xl text-white/70">/100</span>
          </div>
          <h3 className="mt-6 text-2xl lg:text-3xl font-black tracking-tight">
            We identified <span className="underline decoration-white/60">3 ranking constraints</span> holding this business back.<br />
            Want the exact plan?
          </h3>
          <p className="mt-3 text-white/90 max-w-md mx-auto text-sm lg:text-base">
            Free 15-minute strategy session. We&apos;ll walk you through the highest-ROI improvements, competitor weaknesses, and a 90-day roadmap.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-4 font-bold text-base rounded-lg bg-white transition-all hover:-translate-y-0.5 hover:shadow-2xl"
              style={{ color: PRIMARY }}
              data-testid="demo-cta-book"
            >
              <CalendarBlank size={18} weight="bold" /> Book Strategy Session
              <ArrowRight size={14} weight="bold" />
            </a>
            {!brand && (
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-7 py-4 font-bold text-base rounded-lg border-2 border-white/80 text-white transition-all hover:bg-white/10"
                data-testid="demo-cta-pricing"
              >
                See Pricing
              </Link>
            )}
          </div>
        </div>
      </RevealCard>

      {/* AI Growth Blueprint — the AI as consultant, not auditor.
          Each recommendation answers four questions:
            1. What We Found (title)
            2. Why This Matters
            3. How To Improve It  ← execution, not just diagnosis
            4. Expected Business Outcome + Impact */}
      <RevealCard delay={2200} testId="reveal-plan">
        <Section label="AI Growth Operating System" icon={<Lightning size={14} weight="fill" />} accentColor={ACCENT}>
          <p className="text-sm text-zinc-400 mb-3 leading-relaxed">
            Not a 40-item audit checklist. The <span className="text-white font-semibold">three specific changes</span> most likely to
            move {form.business_name || "this business"} closer to Google&apos;s Top 3 for <span className="text-white font-semibold">{form.service || "its primary service"}</span>{form.city ? <> in <span className="text-white font-semibold">{form.city}</span></> : null} over the next 90 days.
          </p>
          {/* AI analysis signature — reinforces this is a judgment for THIS business */}
          <div className="flex items-start gap-2 text-[11px] leading-relaxed mb-5 p-3 rounded-lg" style={{ background: `${ACCENT}0F`, border: `1px solid ${ACCENT}33`, color: "#a1a1aa" }}>
            <Brain size={13} weight="fill" style={{ color: ACCENT, marginTop: 2 }} />
            <span>
              <span style={{ color: ACCENT, fontWeight: 700 }}>AI Ranking Intelligence · </span>
              Every business receives a unique AI Growth Operating System based on its competitive landscape, local market,
              and ranking profile. Recommendations are prioritized by estimated business impact — not by a generic SEO checklist.
            </span>
          </div>
          <div className="space-y-3">
            {buildBlueprint(form).map((rec, i) => {
              const impactColor = rec.impact === "HIGH" ? "#EF4444" : rec.impact === "MEDIUM" ? "#F59E0B" : ACCENT;
              return (
                <div
                  key={rec.key}
                  className="rounded-xl p-4 lg:p-5 border transition-all hover:border-white/20"
                  style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}
                  data-testid={`roi-item-${i}`}
                >
                  {/* Header: rank + title + impact & confidence chips */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-black text-sm tabular-nums"
                        style={{ background: `${ACCENT}22`, color: ACCENT }}
                      >
                        {i + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-zinc-500 mb-0.5">What we found</div>
                        <div className="text-base lg:text-lg font-black text-white leading-tight">{rec.title}</div>
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                      <div
                        className="px-2.5 py-1 rounded-md text-[10px] font-black tracking-[0.15em]"
                        style={{ background: `${impactColor}22`, color: impactColor, border: `1px solid ${impactColor}55` }}
                      >
                        {rec.impact} IMPACT
                      </div>
                      <div className="text-[10px] font-mono tabular-nums text-zinc-400" data-testid={`roi-conf-${i}`}>
                        <span className="text-zinc-500">Ranking confidence: </span>
                        <span className="font-bold text-white">{rec.confidence}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Why + How + Outcome */}
                  <div className="mt-4 pl-11 space-y-4">
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1">Why this matters</div>
                      <p className="text-sm text-zinc-300 leading-relaxed">{rec.why}</p>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1.5">How to improve it</div>
                      <ul className="space-y-1.5">
                        {rec.how.map((step, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed">
                            <Check size={14} weight="bold" style={{ color: ACCENT, marginTop: 3 }} />
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1">Expected business outcome</div>
                      <p className="text-sm text-zinc-300 leading-relaxed">{rec.outcome}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
            <ChartLineUp size={12} weight="bold" className="inline mr-1.5" />
            RankTrust behaves like an experienced consultant — it doesn&apos;t just tell you what&apos;s wrong, it tells you exactly what to do first and what business outcome to expect. Full 90-day sequencing and weekly task cadence unlock in your strategy session.
          </p>
        </Section>
      </RevealCard>
      {/* ============ MIC-DROP: AI Next-Action Panel ============ */}
      <NextActionPanel
        form={form}
        audit={audit}
        realCheck={realCheck}
        primary={PRIMARY}
        ctaUrl={ctaUrl}
      />
    </div>
  );
}

/* ----------------- Industry Intelligence Layer -----------------
   Detect the industry from the free-text service field so every AI output
   speaks the language of that specific business. Deterministic — no LLM calls.
------------------------------------------------------------------- */
function detectIndustry(service) {
  const s = (service || "").toLowerCase().trim();
  if (/dent|orthod|invis|implant|smile|hygien/.test(s)) return {
    label: "dental practice",
    customerNoun: "new patients",
    services: ["new patients", "emergency dentistry", "Invisalign", "implants", "insurance & financing", "online appointment scheduling"],
    websiteFocus: [
      { title: "Insurance we accept + 2-click verify",        detail: "Patients drop off if they can&apos;t tell whether you take their plan. Surface every insurer above the fold.", impact: "+21% appointment rate" },
      { title: "Online appointment scheduler above the fold", detail: "One-click booking beats phone tag every time — especially for new-patient searches at 9pm.",              impact: "+27% new patients" },
      { title: "Before/after treatment gallery",              detail: "Implants, Invisalign, veneers — real cases build trust faster than any copy can.",                          impact: "+18% consult conversion" },
      { title: "Same-day emergency callout",                  detail: "'Emergency dentist near me' is the highest-intent local search. Own the top of every service page.",       impact: "Captures urgent demand" },
      { title: "Doctor bios + treatment expertise",           detail: "Patients choose the dentist, not the practice. Modern bios with credentials + photos convert.",              impact: "+14% trust conversion" },
    ],
    proposalExtras: ["New-patient acquisition tracking", "Insurance-keyword coverage audit", "Emergency-search visibility monitoring"],
    prospectStrengths: [
      "Excellent patient reputation",
      "Underserved emergency-search demand",
      "High lifetime value per patient",
      "Weak competitors on Invisalign & implants",
      "Strong close probability",
    ],
  };
  if (/roof|storm|gutter|shingle/.test(s)) return {
    label: "roofing company",
    customerNoun: "jobs",
    services: ["storm damage response", "insurance claim assistance", "financing", "emergency repairs", "project gallery"],
    websiteFocus: [
      { title: "Storm-damage landing page with instant inspection",   detail: "The first roofer to answer after a storm wins. Add a 'free storm inspection in 24h' offer.", impact: "+38% storm-lead capture" },
      { title: "Insurance claim navigator",                            detail: "Most homeowners don&apos;t know how to file. Guide them and you become the roofer they trust.",  impact: "+22% close rate" },
      { title: "Financing options above the fold",                    detail: "'0% for 12 months' unlocks larger jobs that hesitant homeowners defer.",                        impact: "+18% avg job size" },
      { title: "Project photo gallery by neighborhood",               detail: "'We just did a roof on your street' is the fastest local trust builder there is.",             impact: "+16% conversion" },
      { title: "Emergency-repair click-to-call bar",                  detail: "Storm season = urgency. A sticky mobile call bar captures the panicked homeowner instantly.",   impact: "+24% urgent leads" },
    ],
    proposalExtras: ["Storm-season lead surge preparation", "Insurance-claim keyword coverage", "Neighborhood-level ranking targeting"],
    prospectStrengths: [
      "Strong existing reputation",
      "Massive storm-season revenue upside",
      "Underranked for insurance claim searches",
      "Weak local competitors on financing",
      "Strong close probability",
    ],
  };
  if (/law|attorn|lawyer|legal|counsel/.test(s)) return {
    label: "law firm",
    customerNoun: "case consultations",
    services: ["free consultations", "practice-area pages", "case results", "intake forms", "attorney bios"],
    websiteFocus: [
      { title: "Practice-area pages built for each specialty",  detail: "One page per practice area with local schema — the way Google actually ranks law firms.",     impact: "+30% ranking surface" },
      { title: "Live intake form + free-consultation offer",    detail: "Legal prospects want to talk to someone today. Instant intake + callback beats every competitor.", impact: "+26% consultation rate" },
      { title: "Case-results and settlement showcase",          detail: "Real, anonymized results are the most powerful trust signal in legal marketing.",                impact: "+19% consult-to-client" },
      { title: "Attorney bios with credentials + video",        detail: "Clients hire the attorney, not the firm. Modern bios convert dramatically better.",              impact: "+15% trust conversion" },
      { title: "Sticky mobile 'Free Consultation' bar",         detail: "Legal intent spikes on mobile. A persistent CTA turns bounces into intakes.",                     impact: "+22% mobile leads" },
    ],
    proposalExtras: ["Practice-area ranking targeting", "Consultation-form conversion tracking", "Case-type keyword coverage"],
    prospectStrengths: [
      "Excellent client reputation",
      "Underserved practice-area demand",
      "High case value per client",
      "Weak competitors on consultation UX",
      "Strong close probability",
    ],
  };
  if (/hvac|heat|cool|air condition|furnace|ac repair/.test(s)) return {
    label: "HVAC company",
    customerNoun: "service calls",
    services: ["emergency service", "maintenance plans", "financing", "seasonal tune-ups", "installation quotes"],
    websiteFocus: [
      { title: "24/7 emergency-service callout",                detail: "HVAC search is 90% urgent. Own the emergency intent with a persistent 'available now' badge.",    impact: "+32% urgent calls" },
      { title: "Maintenance plan sign-up above the fold",       detail: "Membership programs = predictable revenue. Make it easy to enroll in one click.",                 impact: "+3x LTV per customer" },
      { title: "Instant financing options",                     detail: "New-system installs are $5–15k. Financing removes the #1 objection at the buying moment.",       impact: "+24% installs" },
      { title: "Seasonal maintenance reminders + booking",      detail: "Automated seasonal outreach turns one-time calls into repeat customers.",                        impact: "+18% repeat rate" },
      { title: "Sticky 'Call Now' bar for mobile searches",     detail: "HVAC emergencies happen on mobile. A persistent call bar wins the panicked homeowner.",            impact: "+28% mobile conversion" },
    ],
    proposalExtras: ["Emergency-search visibility monitoring", "Maintenance-plan conversion tracking", "Seasonal demand forecasting"],
    prospectStrengths: [
      "Strong service reputation",
      "Massive emergency-search demand",
      "High-ticket installation upside",
      "Weak competitors on financing UX",
      "Strong close probability",
    ],
  };
  if (/plumb|drain|leak|sewer|septic/.test(s)) return {
    label: "plumbing company",
    customerNoun: "service calls",
    services: ["emergency plumbing", "drain cleaning", "water heater installs", "financing", "24/7 service"],
    websiteFocus: [
      { title: "24/7 emergency callout above the fold",     detail: "Plumbing search = urgent. Persistent 'call now, on the way in 60 min' badge wins the job.",         impact: "+34% urgent leads" },
      { title: "Instant quote / callback widget",            detail: "Homeowners want an answer now, not a form submission. Live callback within 5 min converts 3× higher.", impact: "+27% conversion" },
      { title: "Service-area map + neighborhood coverage",   detail: "Local trust: 'we serve your zip'. Explicit coverage removes hesitation instantly.",                    impact: "+15% call rate" },
      { title: "Financing for larger jobs (water heaters, repipes)", detail: "Big-ticket jobs stall without financing. Adding it unlocks $5k+ installs.",                    impact: "+22% avg job size" },
      { title: "Sticky mobile 'Call Now' bar",              detail: "Plumbing emergencies happen on phones. A persistent call bar wins every mobile visitor.",              impact: "+28% mobile calls" },
    ],
    proposalExtras: ["Emergency-search visibility monitoring", "Service-area keyword coverage", "Seasonal demand tracking"],
    prospectStrengths: [
      "Strong service reputation",
      "High emergency-search demand",
      "High-ticket water heater / repipe upside",
      "Weak competitors on financing",
      "Strong close probability",
    ],
  };
  if (/medspa|botox|filler|aesthet|derm|skin|laser/.test(s)) return {
    label: "med spa",
    customerNoun: "new clients",
    services: ["Botox & fillers", "membership programs", "consultation booking", "before/after gallery", "financing"],
    websiteFocus: [
      { title: "Before/after gallery by treatment",             detail: "Aesthetic patients buy with their eyes. A curated gallery is the #1 conversion driver.",             impact: "+31% booking rate" },
      { title: "Membership program above the fold",             detail: "Recurring revenue is everything in med spa. Make membership one-click easy.",                        impact: "+3x LTV per client" },
      { title: "Live booking with treatment-specific pages",    detail: "Botox, Kybella, laser — each treatment gets its own optimized page + booking flow.",                 impact: "+24% conversion" },
      { title: "Financing options for larger treatments",       detail: "Multi-treatment plans stall without financing. Adding it unlocks $2k+ purchases.",                   impact: "+18% avg ticket" },
      { title: "Sticky mobile 'Book Now' bar",                  detail: "Aesthetic browsing happens on mobile at night. A persistent CTA captures decision moments.",         impact: "+22% mobile bookings" },
    ],
    proposalExtras: ["Treatment-specific ranking targeting", "Membership-conversion tracking", "Aesthetic-search visibility"],
    prospectStrengths: [
      "Excellent client reputation",
      "High-margin treatment upside",
      "Underserved membership demand",
      "Weak competitors on booking UX",
      "Strong close probability",
    ],
  };
  // Generic fallback for any other local business
  return {
    label: "local business",
    customerNoun: "customers",
    services: ["core services", "consultations", "reviews & reputation", "online booking", "financing options"],
    websiteFocus: [
      { title: "Hero rewritten for local intent",              detail: "A first-fold that answers 'why choose you in this city' in 3 seconds.",                       impact: "+18% call-through rate" },
      { title: "Live review widget above the fold",             detail: "Real-time star average + latest reviews — the #1 trust signal that moves visitors to call.",  impact: "+22% trust conversion" },
      { title: "Service pages built for each offering",         detail: "One dedicated page per service with local schema and clear pricing anchors.",                  impact: "+30% ranking surface area" },
      { title: "Sticky mobile call-to-action bar",             detail: "Persistent Call · Text · Directions bar on mobile — no more lost intent.",                      impact: "+14% mobile lead capture" },
      { title: "Speed + Core Web Vitals pass",                 detail: "Page load pulled under 2 seconds. Google rewards it, users don&apos;t bounce.",                   impact: "Lower bounce, higher rank" },
    ],
    proposalExtras: ["Category-keyword coverage audit", "Service-area ranking targeting", "Reputation-velocity tracking"],
    prospectStrengths: [
      "Solid existing reputation",
      "Large visibility gap in the local pack",
      "High revenue opportunity",
      "Weak local competitors",
      "Strong close probability",
    ],
  };
}

/* Deterministic prospect scoring — reputation stars + close probability derived from audit signals */
function scoreProspect(audit) {
  const { trust, relevance } = audit;
  const overall = (trust + relevance) / 2; // 55-79 range in the mock
  const closeProb = Math.min(94, Math.max(62, Math.round(overall + 18))); // 73-94
  const reputationStars = Math.min(5, Math.max(3, Math.round(trust / 18))); // 3-5 stars
  const closeLabel = closeProb >= 85 ? "Very High" : closeProb >= 75 ? "High" : "Medium-High";
  return { closeProb, reputationStars, closeLabel };
}

/* ----------------- Next Action Panel (mic-drop) ----------------- */
const NEXT_ACTIONS = [
  { key: "script",       label: "Generate AI Sales Script",           icon: Rocket,         accent: "#65CE95" },
  { key: "transform",    label: "Show AI Website Transformation",     icon: PaintBrush,     accent: "#A78BFA" },
  { key: "revenue",      label: "Predict Revenue Growth",             icon: ChartLineUp,    accent: "#38BDF8" },
  { key: "offer",        label: "Recommend Best Service to Pitch",    icon: CurrencyDollar, accent: "#F59E0B" },
  { key: "proposal",     label: "Generate Client Proposal",           icon: FileText,       accent: "#F472B6" },
  { key: "call",         label: "Start AI Call with IntentBrain",     icon: PhoneCall,      accent: "#EF4444" },
];

function NextActionPanel({ form, audit, realCheck, primary, ctaUrl }) {
  const [openAction, setOpenAction] = useState(null); // action key or null
  const [thinking, setThinking] = useState(false);

  const trigger = (key) => {
    setOpenAction(key);
    setThinking(true);
    setTimeout(() => setThinking(false), 850);
  };

  const close = () => { setOpenAction(null); setThinking(false); };

  return (
    <>
      <RevealCard delay={2600} testId="reveal-next-action">
        <div
          className="rounded-2xl p-6 lg:p-8 border relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(45,91,255,0.08), rgba(101,206,149,0.06))",
            borderColor: "rgba(255,255,255,0.08)",
          }}
        >
          {/* Ambient AI aura */}
          <div
            aria-hidden
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-30"
            style={{ background: primary }}
          ></div>

          <div className="relative">
            <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase mb-3" style={{ color: primary }}>
              <Brain size={14} weight="fill" /> AI Next Action
            </div>
            <h3 className="font-black tracking-tight text-2xl lg:text-3xl leading-tight max-w-2xl">
              Your AI has identified the biggest growth opportunity.
              <br />
              <span className="text-zinc-400 font-bold">Choose the next action.</span>
            </h3>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {NEXT_ACTIONS.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.key}
                    onClick={() => trigger(a.key)}
                    data-testid={`next-action-${a.key}`}
                    className="group flex items-center gap-3 text-left rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 p-4 transition-all hover:-translate-y-0.5"
                  >
                    <div
                      className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ background: `${a.accent}22`, color: a.accent }}
                    >
                      <Icon size={18} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white leading-tight">{a.label}</div>
                      <div className="text-[11px] font-mono text-zinc-500 mt-0.5 group-hover:text-zinc-400">
                        Tap to preview
                      </div>
                    </div>
                    <ArrowRight size={14} weight="bold" className="text-zinc-500 group-hover:text-white transition-colors" />
                  </button>
                );
              })}
            </div>

            <p className="mt-6 text-xs font-mono text-zinc-500 max-w-2xl leading-relaxed">
              Each action below is the next step in the same workflow — analyze the business, identify opportunities, recommend services, build the strategy, prepare the proposal, forecast revenue, then hand it off to your AI voice agent. You&apos;re not opening reports. You&apos;re running a growth advisor.
            </p>
          </div>
        </div>
      </RevealCard>

      {/* AI Action Preview Modal */}
      {openAction && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
          data-testid="next-action-modal"
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-950 text-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
              data-testid="next-action-close"
              aria-label="Close"
            >
              <X size={16} weight="bold" />
            </button>
            <div className="p-6 lg:p-8">
              {thinking ? (
                <div className="py-16 text-center" data-testid="next-action-thinking">
                  <div className="inline-flex items-center gap-3 text-sm font-mono text-zinc-400">
                    <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                    Your AI Growth Advisor is preparing this…
                  </div>
                </div>
              ) : (
                <ActionContent
                  actionKey={openAction}
                  form={form}
                  audit={audit}
                  realCheck={realCheck}
                  primary={primary}
                  ctaUrl={ctaUrl}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ----- Per-action deterministic preview content ----- */
// Contextual CTA labels — each modal ends with a business-outcome CTA, not "Unlock in Strategy Session"
const ACTION_CTA_LABEL = {
  script:    "Unlock the Complete AI Sales Conversation",
  transform: "Build the Website Transformation Plan",
  revenue:   "See the Full 90-day Growth Model",
  offer:     "See the Complete Service Recommendation",
  proposal:  "Unlock the Business Growth Blueprint",
};

function ActionContent({ actionKey, form, audit, primary, ctaUrl }) {
  const meta = NEXT_ACTIONS.find((a) => a.key === actionKey);
  const Icon = meta.icon;
  const biz = form.business_name || "Your business";
  const city = form.city || "your city";
  const { trust, relevance, missedSearches, missedLeads } = audit;
  const monthlyRev = missedLeads * 250; // avg local ticket
  const projected90 = monthlyRev * 3;
  const serviceLabel = form.service || "your service";
  const industry = detectIndustry(form.service);
  const customerNoun = industry.customerNoun;

  return (
    <div data-testid={`action-content-${actionKey}`}>
      <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase mb-2" style={{ color: meta.accent }}>
        <Icon size={14} weight="fill" /> {meta.label}
      </div>
      <h4 className="font-black tracking-tight text-2xl lg:text-3xl leading-tight mb-5">
        {actionKey === "script"    && <>The complete sales conversation for <span style={{ color: meta.accent }}>{biz}</span></>}
        {actionKey === "transform" && <>How AI will rebuild <span style={{ color: meta.accent }}>{biz}</span>&apos;s website into a lead engine</>}
        {actionKey === "revenue"   && <>Projected 90-day growth model for <span style={{ color: meta.accent }}>{biz}</span></>}
        {actionKey === "offer"     && <>Which services will grow <span style={{ color: meta.accent }}>{biz}</span> the fastest</>}
        {actionKey === "proposal"  && <>Business Growth Blueprint for <span style={{ color: meta.accent }}>{biz}</span></>}
        {actionKey === "call"      && <>Your <span style={{ color: meta.accent }}>AI Call Package</span> is ready</>}
      </h4>

      {/* --- SCRIPT --- Real SDR opener, prospect snapshot, conversation strategy */}
      {actionKey === "script" && (
        <div className="space-y-4">
          {/* Prospect Snapshot — reset context before the script */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: meta.accent }}>Prospect Snapshot</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <div className="flex justify-between gap-2 border-b border-white/5 py-1"><span className="text-zinc-500 text-xs">Business</span><span className="font-semibold text-white text-right truncate">{biz}</span></div>
              <div className="flex justify-between gap-2 border-b border-white/5 py-1"><span className="text-zinc-500 text-xs">Market</span><span className="font-semibold text-white text-right truncate">{city}</span></div>
              <div className="flex justify-between gap-2 border-b border-white/5 py-1"><span className="text-zinc-500 text-xs">Leads lost / mo</span><span className="font-semibold text-white text-right">~{missedLeads} {customerNoun}</span></div>
              <div className="flex justify-between gap-2 border-b border-white/5 py-1"><span className="text-zinc-500 text-xs">Revenue at stake</span><span className="font-semibold text-white text-right">${monthlyRev.toLocaleString()} / mo</span></div>
            </div>
          </div>

          {/* Conversation Strategy */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: meta.accent }}>Conversation Strategy</div>
            <ul className="text-sm text-zinc-300 space-y-1.5">
              <li><b className="text-white">Goal:</b> book a 15-minute call to walk them through their growth opportunity.</li>
              <li><b className="text-white">Tone:</b> peer-to-peer, curious, zero pitch. You&apos;re bringing insight, not selling software.</li>
              <li><b className="text-white">Angle:</b> lead with the revenue they&apos;re losing to top-3 competitors — not with SEO tactics.</li>
            </ul>
          </div>

          {/* The Opener — real SDR voice, no Trust Score reference */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-sm leading-relaxed text-zinc-200">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: meta.accent }}>The Opener</div>
            &ldquo;Hey — is this the owner of <b>{biz}</b>? Quick question, and I promise I&apos;m not selling anything on this call. I was looking at how {industry.label !== "local business" ? industry.label + "s" : "local businesses"} show up in {city} and noticed <b>{biz}</b> is right outside the top three — which means about <b style={{ color: meta.accent }}>{missedLeads} {customerNoun} a month</b> are calling your competitors instead of you.
            <br /><br />
            I&apos;ve already mapped out exactly why that&apos;s happening and the three things that would move you into the top three. Worth 15 minutes to walk you through it?&rdquo;
          </div>

          {/* Objection preview */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-zinc-300">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: meta.accent }}>What&apos;s included in the full conversation</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4">
              {["Opener + hook", "Discovery questions", "Objection handling (busy, cost, skeptical)", "Value framing", "Close + calendar invite", "Voicemail + follow-up email"].map((t, i) => (
                <li key={i} className="flex items-center gap-2"><CheckCircle size={13} weight="fill" style={{ color: meta.accent }} /><span>{t}</span></li>
              ))}
            </ul>
          </div>

          <div className="text-[11px] text-zinc-500 leading-relaxed">
            Written by your AI Growth Advisor from the live audit — personalized to <b className="text-zinc-300">{biz}</b>, not a template. Copy the exact language on your next call.
          </div>
        </div>
      )}

      {/* --- TRANSFORM --- Business-specific website transformation with conversion impact */}
      {actionKey === "transform" && (
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-zinc-300 leading-relaxed">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: meta.accent }}>Why this matters</div>
            {biz}&apos;s current site is likely losing {customerNoun} before they even call. Even if the profile ranks, a slow or unfocused website leaks the conversion. The changes below are what your AI Growth Advisor recommends first for a {industry.label} in {city} — highest business impact per dollar of effort.
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: meta.accent }}>AI Transformation Roadmap</div>
            <ul className="space-y-3">
              {industry.websiteFocus.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 shrink-0 rounded-md flex items-center justify-center text-[11px] font-black tabular-nums mt-0.5" style={{ background: `${meta.accent}22`, color: meta.accent }}>{i + 1}</div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-white">{s.title}</div>
                    <div className="text-xs text-zinc-400 leading-relaxed mt-0.5" dangerouslySetInnerHTML={{ __html: s.detail }} />
                    <div className="text-[11px] font-semibold mt-1" style={{ color: meta.accent }}>Expected impact: {s.impact}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border p-4 text-sm text-zinc-200 leading-relaxed" style={{ borderColor: `${meta.accent}55`, background: `${meta.accent}0e` }}>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: meta.accent }}>Expected business outcome</div>
            Once this rollout is live, {biz} typically converts <b>1.6&ndash;2.2× more</b> of the traffic already coming in — before ranking improvements even kick in.
          </div>
        </div>
      )}

      {/* --- REVENUE --- Same design, credibility improved via assumptions */}
      {actionKey === "revenue" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "30 days", val: monthlyRev },
              { label: "60 days", val: monthlyRev * 2 },
              { label: "90 days", val: projected90 },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1">{s.label}</div>
                <div className="text-2xl lg:text-3xl font-black tabular-nums" style={{ color: meta.accent }}>
                  +${s.val.toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {/* How we calculate this */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: meta.accent }}>How your AI Advisor calculates this</div>
            <ul className="space-y-1.5 text-sm text-zinc-300">
              <li className="flex justify-between gap-3 border-b border-white/5 py-1"><span className="text-zinc-500 text-xs">Missed local searches / mo</span><span className="font-semibold text-white tabular-nums">{missedSearches.toLocaleString()}</span></li>
              <li className="flex justify-between gap-3 border-b border-white/5 py-1"><span className="text-zinc-500 text-xs">Local pack click-through</span><span className="font-semibold text-white tabular-nums">~44%</span></li>
              <li className="flex justify-between gap-3 border-b border-white/5 py-1"><span className="text-zinc-500 text-xs">Lead → customer conversion</span><span className="font-semibold text-white tabular-nums">~18%</span></li>
              <li className="flex justify-between gap-3 border-b border-white/5 py-1"><span className="text-zinc-500 text-xs">Avg customer value in {city}</span><span className="font-semibold text-white tabular-nums">$250</span></li>
              <li className="flex justify-between gap-3 py-1"><span className="text-zinc-500 text-xs">Recovered {customerNoun} / mo (30d)</span><span className="font-semibold text-white tabular-nums">~{missedLeads}</span></li>
            </ul>
          </div>

          <div className="text-xs text-zinc-500 leading-relaxed">
            These are conservative projections built from local benchmarks for {city} businesses, not guarantees. Actual results depend on execution speed and market conditions. Compounds monthly as ranking climbs.
          </div>
        </div>
      )}

      {/* --- OFFER --- AI recommendation confidence, no fixed pricing, why + impact + timeline */}
      {actionKey === "offer" && (
        <div className="space-y-3">
          <div className="text-sm text-zinc-400 leading-relaxed">
            Your AI Growth Advisor ranked these based on <b className="text-white">business impact</b>, <b className="text-white">speed to results</b>, and how well they fit {biz}&apos;s current constraints as a {industry.label}.
          </div>
          {[
            {
              name: "Local Ranking Acceleration",
              confidence: 96,
              why: `Directly closes the gap between ${biz} and the top-3 ${industry.label}s ranking in ${city} — highest-leverage move first.`,
              impact: `Move into the top-3 local pack, capture the ~44% of ${customerNoun} who never scroll past it.`,
              timeline: "First ranking movement in 30–45 days",
            },
            {
              name: industry.label === "local business" ? "Local Content Engine" : `Local Content Engine for ${industry.label}s`,
              confidence: 84,
              why: `Fills the relevance gap the AI detected — Google needs to see fresh, ${industry.label}-specific content weekly (${industry.services.slice(0, 3).join(", ")}, and more).`,
              impact: "Compounds the ranking work — every week you stay top-3 without extra effort.",
              timeline: "Compounding gains 60–90 days in",
            },
            {
              name: "AI Answer-Engine Visibility",
              confidence: 71,
              why: `Extends ${biz} beyond Google Maps into ChatGPT, Claude, and Perplexity — where ${customerNoun} increasingly ask "who&apos;s the best ${industry.label} in ${city}?"`,
              impact: `Get recommended by AI assistants when ${customerNoun} skip Google entirely.`,
              timeline: "Early mentions in 45–60 days",
            },
          ].map((o, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center font-black tabular-nums text-sm" style={{ background: `${meta.accent}22`, color: meta.accent }}>
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="font-bold text-white text-base">{o.name}</div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-xs tabular-nums font-bold" style={{ color: meta.accent }}>{o.confidence}%</div>
                      <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">AI confidence</div>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs leading-relaxed">
                    <div><span className="text-zinc-500 font-mono uppercase text-[10px] tracking-wider">Why #{i + 1}: </span><span className="text-zinc-300" dangerouslySetInnerHTML={{ __html: o.why }} /></div>
                    <div><span className="text-zinc-500 font-mono uppercase text-[10px] tracking-wider">Business impact: </span><span className="text-zinc-300">{o.impact}</span></div>
                    <div><span className="text-zinc-500 font-mono uppercase text-[10px] tracking-wider">Timeline: </span><span className="font-semibold" style={{ color: meta.accent }}>{o.timeline}</span></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="text-[11px] text-zinc-500 leading-relaxed">
            Pricing is set inside your strategy session — the right fit depends on scope, timeline, and the outcome {biz} wants first.
          </div>
        </div>
      )}

      {/* --- PROPOSAL --- Executive Growth Proposal, opportunity-led */}
      {actionKey === "proposal" && (
        <div className="space-y-4">
          {/* Opportunity Summary */}
          <div className="rounded-xl border p-5 text-sm leading-relaxed" style={{ borderColor: `${meta.accent}55`, background: `${meta.accent}0e` }}>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: meta.accent }}>The Opportunity</div>
            <div className="text-zinc-100">
              {biz} is <b className="text-white">outside the top-3</b> for local {industry.label} searches in {city}. That single gap is quietly costing the business roughly <b className="text-white">${monthlyRev.toLocaleString()} in revenue every month</b> — {customerNoun} who found their competitors instead. This proposal captures that revenue back inside 90 days.
            </div>
          </div>

          {/* Executive summary + deliverables */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: meta.accent }}>Executive Summary</div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                A focused 90-day engagement designed to move {biz} into the local top-3, convert more of the {customerNoun} already visiting, and build a compounding presence across Google and AI search — all tuned specifically for a {industry.label}.
              </p>
            </div>

            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: meta.accent }}>What&apos;s Included</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-sm text-zinc-200">
                {[
                  "Full Google Business Profile rebuild",
                  "Weekly local content + Q&A publishing",
                  "Review velocity engine + reply automation",
                  "Website conversion upgrade",
                  "Competitor tracking + gap alerts",
                  "AI answer-engine visibility tracking",
                  ...industry.proposalExtras,
                  "Monthly executive scorecard",
                ].slice(0, 8).map((item, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle size={13} weight="fill" style={{ color: meta.accent }} /><span>{item}</span></li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: meta.accent }}>Expected Outcomes (90 days)</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-[10px] font-mono uppercase text-zinc-500">Rank</div>
                  <div className="text-lg font-black text-white">Top-3 local</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-[10px] font-mono uppercase text-zinc-500">{customerNoun === "new patients" ? "New patients" : customerNoun === "customers" ? "New customers" : `New ${customerNoun}`}</div>
                  <div className="text-lg font-black text-white tabular-nums">+{Math.round(missedLeads * 1.6)}</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-[10px] font-mono uppercase text-zinc-500">Revenue lift</div>
                  <div className="text-lg font-black tabular-nums" style={{ color: meta.accent }}>+${projected90.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-[11px] text-zinc-500 leading-relaxed">
            The full whitelabeled PDF (with your agency&apos;s branding, terms, and signature block) is generated in your workspace.
          </div>
        </div>
      )}

      {/* --- CALL --- */}
      {actionKey === "call" && (
        <IntentBrainCallCard
          biz={biz}
          city={city}
          form={form}
          audit={audit}
          accent={meta.accent}
        />
      )}

      {/* Shared CTA — hidden for the call action, which has its own real handoff CTA. Contextual copy per modal. */}
      {actionKey !== "call" && (
        <div className="mt-6 flex flex-wrap items-center gap-3 pt-5 border-t border-white/10">
          <a
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 font-bold text-sm rounded-lg transition-all hover:-translate-y-0.5"
            style={{ background: primary, color: "#fff" }}
            data-testid={`action-cta-${actionKey}`}
          >
            <CalendarBlank size={14} weight="bold" /> {ACTION_CTA_LABEL[actionKey] || "Unlock in Strategy Session"}
            <ArrowRight size={14} weight="bold" />
          </a>
          <span className="text-[11px] font-mono text-zinc-500">15-min strategy session · no card required</span>
        </div>
      )}
    </div>
  );
}

/* ----- IntentBrain Call Card (real handoff — staged sequence + backend POST) ----- */
const HANDOFF_STAGES = [
  "Briefing IntentBrain on the business",
  "Loading the personalized sales strategy",
  "Handing the prospect to your AI voice agent",
  "IntentBrain is ready to make the call",
];

function IntentBrainCallCard({ biz, city, form, audit, accent }) {
  const [phase, setPhase] = useState("briefing"); // briefing → dispatching → done
  const [completedStages, setCompletedStages] = useState(0);
  const [result, setResult] = useState(null); // backend response

  const { trust, relevance, missedSearches, missedLeads } = audit;
  const overall = Math.round((trust + relevance) / 2);
  const industry = detectIndustry(form.service);
  const { closeProb, reputationStars, closeLabel } = scoreProspect(audit);
  const monthlyRev = missedLeads * 250;
  const bestOffer = industry.label === "local business" ? "Local Ranking Acceleration" : `Local Ranking Acceleration for ${industry.label}s`;
  const primaryGrowthOpportunity = `Recover the ~${missedLeads} ${industry.customerNoun} calling top-3 competitors in ${city}`;
  const currentMapsPosition = "Outside the top-3 local pack";

  const startHandoff = async () => {
    setPhase("dispatching");
    setCompletedStages(0);

    // Fire the real backend call in parallel with the staged animation.
    // NOTE: this packet mirrors the AI Call Package the agency owner sees in the modal,
    // so IntentBrain receives the same intelligence the human just reviewed.
    const packet = {
      // Prospect
      business_name: biz,
      city,
      state: form.state || null,
      service: form.service || null,
      industry_label: industry.label,
      customer_noun: industry.customerNoun,

      // Diagnosis
      trust_score: trust,
      relevance_score: relevance,
      overall_score: overall,
      current_maps_position: currentMapsPosition,
      missed_searches_per_month: missedSearches,
      missed_leads_per_month: missedLeads,
      revenue_opportunity_monthly: monthlyRev,

      // Prospect Scoring
      close_probability: closeProb,
      close_strength_label: closeLabel,
      reputation_stars: reputationStars,
      why_this_prospect: industry.prospectStrengths,

      // Sales Strategy
      best_first_offer: bestOffer,
      primary_growth_opportunity: primaryGrowthOpportunity,
      opening_line: `Hey — is this the owner of ${biz}? Quick question, and I promise I'm not selling anything on this call. I was looking at how ${industry.label}s show up in ${city} and noticed ${biz} is right outside the top three — which means about ${missedLeads} ${industry.customerNoun} a month are calling your competitors instead of you.`,
      conversation_strategy: {
        goal: "Book a 15-minute call to walk them through their growth opportunity.",
        tone: "Peer-to-peer, curious, zero pitch. Bringing insight, not selling software.",
        angle: "Lead with the revenue they're losing to top-3 competitors — not with SEO tactics.",
      },
      objection_strategy: [
        "Too busy right now",
        "Costs too much / not the right time",
        "Already have someone handling marketing",
        "Skeptical about SEO / ranking claims",
      ],
      top_competitors: ["Top-3 Competitor A", "Top-3 Competitor B", "Top-3 Competitor C"],
    };

    const backendPromise = axios
      .post(`${API}/demo/intentbrain-handoff`, packet)
      .then((r) => r.data)
      .catch((err) => ({
        status: "delivery_failed",
        live: false,
        message: err?.message || "Network error while contacting the platform.",
      }));

    // Staged reveal — 600ms per checkpoint, stage 3 waits for the backend
    for (let i = 0; i < HANDOFF_STAGES.length; i++) {
      // Stage 3 (index 2, "Sending context to IntentBrain") awaits the real network call
      if (i === 2) {
        const [_, res] = await Promise.all([
          new Promise((r) => setTimeout(r, 600)),
          backendPromise,
        ]);
        setResult(res);
      } else {
        await new Promise((r) => setTimeout(r, 600));
      }
      setCompletedStages(i + 1);
    }
    setPhase("done");
  };

  return (
    <div className="space-y-4" data-testid="intentbrain-call-card">
      {/* Subheadline — the mic drop */}
      <div className="text-sm text-zinc-300 leading-relaxed">
        Everything IntentBrain needs has already been prepared. Your AI Growth Advisor has completed the full workflow — <b className="text-white">{biz}</b> is ready to receive the call.
      </div>

      {/* Two-column top row: Why This Prospect + Close Probability */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Why This Prospect */}
        <div className="sm:col-span-2 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: accent }}>Why This Prospect</div>
            <div className="text-xs tabular-nums" style={{ color: accent }}>
              {"★".repeat(reputationStars)}<span className="text-zinc-600">{"★".repeat(5 - reputationStars)}</span>
            </div>
          </div>
          <ul className="space-y-1.5">
            {industry.prospectStrengths.map((s, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-zinc-200">
                <CheckCircle size={13} weight="fill" style={{ color: accent }} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Close Probability metric */}
        <div className="rounded-xl border p-4 flex flex-col items-center justify-center text-center" style={{ borderColor: `${accent}55`, background: `${accent}10` }} data-testid="close-probability">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: accent }}>Close Probability</div>
          <div className="text-5xl font-black tabular-nums leading-none" style={{ color: accent }}>{closeProb}%</div>
          <div className="text-xs font-bold text-white mt-1.5">{closeLabel}</div>
          <div className="text-[10px] text-zinc-400 mt-1">Worth calling</div>
        </div>
      </div>

      {/* What IntentBrain Already Knows — 8 items */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: accent }}>
          What IntentBrain already knows
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          {[
            ["Business", biz],
            ["Current Maps Position", currentMapsPosition],
            ["Revenue Opportunity", `$${monthlyRev.toLocaleString()} / mo`],
            ["Primary Growth Opportunity", `+${missedLeads} ${industry.customerNoun} / mo`],
            ["Best First Offer", bestOffer],
            ["Conversation Strategy", "Peer-to-peer, revenue-led"],
            ["Objection Handling", "Busy · Cost · Skeptical · Trust"],
            ["Complete Sales Conversation", "Opener → discovery → close"],
          ].map(([k, v], i) => (
            <div key={i} className="flex items-baseline justify-between gap-3 border-b border-white/5 py-1.5">
              <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">{k}</span>
              <span className="text-sm font-bold text-white text-right truncate">{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Phase 1: briefing — offer the handoff */}
      {phase === "briefing" && (
        <>
          <div className="rounded-lg border p-4 text-sm text-zinc-200 leading-relaxed" style={{ borderColor: `${accent}55`, background: `${accent}11` }}>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1.5" style={{ color: accent }}>Zero preparation required</div>
            Once approved, IntentBrain calls this prospect using the complete business intelligence prepared by RankTrust — including the recommended offer, personalized conversation strategy, objection handling, and sales script.
            <div className="mt-2 text-xs text-zinc-400">You don&apos;t rehearse. You don&apos;t prepare notes. You just approve the handoff.</div>
          </div>
          <button
            onClick={startHandoff}
            data-testid="intentbrain-push-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 font-bold text-base rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-2xl"
            style={{ background: accent, color: "#0a0a0a" }}
          >
            <PhoneCall size={16} weight="fill" /> Hand off to IntentBrain
            <ArrowRight size={14} weight="bold" />
          </button>
        </>
      )}

      {/* Phase 2/3: staged sequence */}
      {phase !== "briefing" && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-2.5" data-testid="handoff-stages">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: accent }}>
            AI Handoff in Progress
          </div>
          {HANDOFF_STAGES.map((label, i) => {
            const done = i < completedStages;
            const active = i === completedStages && phase === "dispatching";
            return (
              <div
                key={i}
                className="flex items-center gap-3 text-sm transition-all"
                style={{ opacity: done ? 1 : active ? 0.9 : 0.35, color: done ? accent : active ? "#fff" : "#71717a" }}
                data-testid={`handoff-stage-${i}`}
              >
                <div className="w-5 flex items-center justify-center">
                  {done ? (
                    <CheckCircle size={16} weight="fill" />
                  ) : active ? (
                    <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <span className="inline-block w-2 h-2 rounded-full bg-zinc-700"></span>
                  )}
                </div>
                <div className={done || active ? "font-semibold" : ""}>{label}{active ? "…" : ""}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Phase 3: final honest state */}
      {phase === "done" && result && (
        <div
          className="rounded-xl border p-5"
          style={{
            borderColor: result.live ? `${accent}55` : "rgba(245,158,11,0.4)",
            background: result.live ? `${accent}11` : "rgba(245,158,11,0.08)",
          }}
          data-testid="handoff-result"
        >
          {result.live ? (
            <div className="flex items-start gap-2.5">
              <CheckCircle size={18} weight="fill" style={{ color: accent, marginTop: 1 }} />
              <div className="flex-1">
                <div className="font-bold text-white text-base mb-1">
                  IntentBrain is live — call queued for {biz}
                </div>
                <div className="text-xs text-zinc-400">Your AI voice agent has full context and will place the call shortly. You&apos;ll get a call summary once the conversation completes.</div>
                {result.packet_id && (
                  <div className="text-[10px] font-mono text-zinc-500 mt-2">
                    Handoff ID: {result.packet_id}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-2.5 mb-3">
                <CheckCircle size={18} weight="fill" style={{ color: accent, marginTop: 1 }} />
                <div>
                  <div className="font-black text-white text-lg leading-tight">Handoff ready for {biz}</div>
                  <div className="text-sm text-zinc-400 mt-0.5">Your AI voice agent has the full briefing.</div>
                </div>
              </div>
              <div className="pl-7">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">Loaded into IntentBrain</div>
                <ul className="space-y-1.5 mb-4">
                  {[
                    "Business context",
                    "Competitive landscape",
                    "Personalized sales strategy",
                    "The offer to lead with",
                    "The full conversation script",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-200">
                      <CheckCircle size={14} weight="fill" style={{ color: accent }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-sm text-zinc-400 mb-4 leading-relaxed">
                  Connect IntentBrain in Agency Hub to place the live call — no manual preparation, no notes to write. Your AI advisor has handled everything.
                </div>
                <Link
                  to="/agency"
                  className="inline-flex items-center gap-2 px-5 py-2.5 font-bold text-sm rounded-lg transition-all hover:-translate-y-0.5"
                  style={{ background: accent, color: "#0a0a0a" }}
                  data-testid="handoff-connect-cta"
                >
                  Activate IntentBrain <ArrowRight size={14} weight="bold" />
                </Link>
                {result.packet_id && (
                  <div className="text-[10px] font-mono text-zinc-500 mt-3">
                    Handoff ID: {result.packet_id}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
function Section({ label, icon, children, accentColor }) {
  const c = accentColor || ACCENT;
  return (
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 lg:p-7">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4 inline-flex items-center gap-1.5" style={{ color: c }}>
        {icon}{label}
      </div>
      {children}
    </div>
  );
}

function ScoreCard({ label, value, caption }) {
  const c = colorForScore(value);
  return (
    <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-6">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="text-5xl lg:text-6xl font-black tabular-nums" style={{ color: c }}>
          <Counter target={value} />
        </div>
        <div className="text-xl font-bold text-zinc-600">/100</div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-zinc-900 overflow-hidden">
        <div className="h-full transition-all duration-[1400ms]" style={{ width: `${value}%`, background: c }}></div>
      </div>
      <div className="mt-3 text-xs text-zinc-500">{caption}</div>
    </div>
  );
}

function CompetitorRow({ name, score, you, max }) {
  const width = Math.round((score / 100) * 100);
  return (
    <div className="flex items-center gap-3" data-testid={you ? "competitor-you" : undefined}>
      <div className="w-44 lg:w-56 text-sm font-semibold truncate" style={{ color: you ? "#fff" : "#a1a1aa" }}>{name}</div>
      <div className="flex-1 h-2 rounded-full bg-zinc-900 overflow-hidden">
        <div
          className="h-full transition-all duration-[1500ms]"
          style={{ width: `${width}%`, background: you ? RED : (score === max ? ACCENT : "#52525b") }}
        ></div>
      </div>
      <div className="w-12 text-right text-sm font-black tabular-nums" style={{ color: you ? RED : "#fff" }}>
        {score}
      </div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-5">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">{label}</div>
      <div className="text-4xl lg:text-5xl font-black tabular-nums" style={{ color }}>
        <Counter target={value} duration={1700} />
      </div>
    </div>
  );
}

function AIBadge({ engine, grade, color }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.03] p-4 text-center">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color }}>{engine}</div>
      <div className="text-4xl font-black" style={{ color }}>{grade}</div>
    </div>
  );
}
